import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '@/hooks/useAuth';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { validatePassword } from '@/utils/validators';
import { emailValidator, requiredValidator } from '@/lib/forms';

export function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm({
        defaultValues: {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
        },
        onSubmit: async ({ value }) => {
            setError('');

            // Validation
            if (value.password !== value.confirmPassword) {
                setError('Passwords do not match');
                return;
            }

            const { isValid } = validatePassword(value.password);
            if (!isValid) {
                setError('Password does not meet requirements');
                return;
            }

            setIsLoading(true);

            try {
                await register(value.email, value.password, value.name);
                navigate('/');
            } catch (err: any) {
                setError(err.message || 'Registration failed');
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <div className="container d-flex justify-content-center align-items-center min-vh-100 py-5">
            <div className="card shadow border-0" style={{ maxWidth: 450, width: '100%' }}>
                <div className="card-body p-4">
                    <div className="text-center mb-4">
                        <h1 className="h3 mb-3 fw-normal">Create Account</h1>
                        <p className="text-muted">Join PlaylistR to collaborate on playlists</p>
                    </div>

                    {error && <div className="alert alert-danger" role="alert">{error}</div>}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <form.Field
                            name="name"
                            validators={{
                                onChange: requiredValidator('Display Name'),
                            }}
                        >
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="nameInput" className="form-label">Display Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="nameInput"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="John Doe"
                                    />
                                    {field.state.meta.errors.length > 0 && (
                                        <div className="text-danger small mt-1">
                                            {field.state.meta.errors[0]}
                                        </div>
                                    )}
                                </div>
                            )}
                        </form.Field>

                        <form.Field
                            name="email"
                            validators={{
                                onChange: emailValidator,
                            }}
                        >
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="emailInput" className="form-label">Email address</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="emailInput"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="name@example.com"
                                    />
                                    {field.state.meta.errors.length > 0 && (
                                        <div className="text-danger small mt-1">
                                            {field.state.meta.errors[0]}
                                        </div>
                                    )}
                                </div>
                            )}
                        </form.Field>

                        <form.Field name="password">
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="passwordInput" className="form-label">Password</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="passwordInput"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Min 8 chars"
                                    />
                                    <PasswordStrengthIndicator password={field.state.value} />
                                </div>
                            )}
                        </form.Field>

                        <form.Field name="confirmPassword">
                            {(field) => (
                                <div className="mb-3">
                                    <label htmlFor="confirmPasswordInput" className="form-label">Confirm Password</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="confirmPasswordInput"
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                        placeholder="Confirm password"
                                    />
                                </div>
                            )}
                        </form.Field>

                        <button
                            type="submit"
                            className="btn btn-primary w-100 py-2 mt-3"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-3">
                        <p className="small text-muted mb-0">
                            Already have an account? <Link to="/login" className="text-decoration-none">Sign in</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
